import {
  ImsPermissions,
  ReturnsAndClaimsPermissions,
} from "@redotech/account-common/permissions";
import type { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";

export const warehouseRoutes: PermissionedRouteObject[] = [
  {
    async lazy() {
      const [
        { WmsProvider },
        { SuppressPrintNotificationsProvider },
        { TaskPlatformProviders },
        { Outlet },
      ] = await Promise.all([
        import("@redotech/redo-merchant-fulfillment-app/common/wms-context"),
        import(
          "@redotech/redo-merchant-fulfillment-app/common/print-queue-context"
        ),
        import(
          "@redotech/redo-merchant-fulfillment-app/tasks/task-platform-providers"
        ),
        import("react-router-dom"),
      ]);

      return {
        element: (
          <WmsProvider>
            <TaskPlatformProviders>
              <SuppressPrintNotificationsProvider>
                <Outlet />
              </SuppressPrintNotificationsProvider>
            </TaskPlatformProviders>
          </WmsProvider>
        ),
      };
    },
    children: [
      {
        index: true,
        handle: { breadcrumb: "Warehouse management" },
        async lazy() {
          const { WarehouseLandingPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/warehouse-landing-page"
          );
          return { Component: WarehouseLandingPage };
        },
      },
      {
        path: "transfer",
        handle: { breadcrumb: "Transfer" },
        async lazy() {
          const [{ TransferProvider }, { Outlet }] = await Promise.all([
            import(
              "@redotech/redo-merchant-fulfillment-app/wms/transfer/transfer-context"
            ),
            import("react-router-dom"),
          ]);
          return {
            element: (
              <TransferProvider>
                <Outlet />
              </TransferProvider>
            ),
          };
        },
        children: [
          {
            index: true,
            async lazy() {
              const { TransferPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/transfer/transfer-page"
              );
              return { Component: TransferPage };
            },
          },
          {
            path: "items",
            handle: { breadcrumb: "Select items" },
            async lazy() {
              const { TransferItemsPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/transfer/transfer-items-page"
              );
              return { Component: TransferItemsPage };
            },
          },
          {
            path: "lost-items",
            handle: { breadcrumb: "Find items" },
            async lazy() {
              const { TransferLostItemsPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/transfer/transfer-lost-items-page"
              );
              return { Component: TransferLostItemsPage };
            },
          },
          {
            path: "route",
            handle: { breadcrumb: "Transfer route" },
            async lazy() {
              const { TransferRoutePage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/transfer/transfer-route-page"
              );
              return { Component: TransferRoutePage };
            },
          },
        ],
      },
      {
        path: "instant-transfer",
        handle: { breadcrumb: "Instant transfer" },
        async lazy() {
          const { InstantTransferPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/instant-transfer/instant-transfer-page"
          );
          return { Component: InstantTransferPage };
        },
      },
      {
        path: "receive-to-bin",
        handle: { breadcrumb: "Receive to bin" },
        async lazy() {
          const { ReceiveToBinPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/receive-to-bin/receive-to-bin-page"
          );
          return { Component: ReceiveToBinPage };
        },
      },
      {
        path: "count",
        handle: { breadcrumb: "Count" },
        children: [
          {
            index: true,
            async lazy() {
              const { CountPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/bin-count/count-page"
              );
              return { Component: CountPage };
            },
          },
          {
            path: "session",
            handle: { breadcrumb: "Count bins" },
            async lazy() {
              const { BinCountSessionPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/bin-count/bin-count-session-page"
              );
              return { Component: BinCountSessionPage };
            },
          },
          {
            path: "sku",
            handle: { breadcrumb: "Count by SKU" },
            async lazy() {
              const { ImsSkuCountPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/bin-count/ims-sku-count-page"
              );
              return { Component: ImsSkuCountPage };
            },
          },
        ],
      },
      {
        path: "receive",
        handle: { breadcrumb: "Receive" },
        children: [
          {
            index: true,
            async lazy() {
              const { ReceivingPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/receiving/receiving-page"
              );
              return { Component: ReceivingPage };
            },
          },
          {
            path: "place",
            handle: { breadcrumb: "Place inventory" },
            async lazy() {
              const [{ TransferProvider }, { Outlet }] = await Promise.all([
                import(
                  "@redotech/redo-merchant-fulfillment-app/wms/transfer/transfer-context"
                ),
                import("react-router-dom"),
              ]);
              return {
                element: (
                  <TransferProvider>
                    <Outlet />
                  </TransferProvider>
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { ReceivingPlacePage } = await import(
                    "@redotech/redo-merchant-fulfillment-app/wms/receiving/receiving-place-page"
                  );
                  return { Component: ReceivingPlacePage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "receive-shipment",
        handle: { breadcrumb: "Receive shipment" },
        async lazy() {
          const { ReceiveShipmentSearchPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/receiving/receive-shipment-search-page"
          );
          return { Component: ReceiveShipmentSearchPage };
        },
      },
      {
        path: "receive-shipment/:shipmentId",
        handle: { breadcrumb: "Receive items" },
        async lazy() {
          const { ReceiveShipmentPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/receiving/receive-shipment-page"
          );
          return { Component: ReceiveShipmentPage };
        },
      },
      {
        path: "replenishment",
        handle: { breadcrumb: "Replenishment" },
        async lazy() {
          const [{ ReplenishmentProvider }, { Outlet }] = await Promise.all([
            import(
              "@redotech/redo-merchant-fulfillment-app/wms/replenishment/replenishment-context"
            ),
            import("react-router-dom"),
          ]);
          return {
            element: (
              <ReplenishmentProvider>
                <Outlet />
              </ReplenishmentProvider>
            ),
          };
        },
        children: [
          {
            index: true,
            async lazy() {
              const { ReplenishmentDashboardPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/replenishment/replenishment-dashboard-page"
              );
              return { Component: ReplenishmentDashboardPage };
            },
          },
          {
            path: "source",
            handle: { breadcrumb: "Source items" },
            async lazy() {
              const { ReplenishmentSourcePage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/replenishment/replenishment-source-page"
              );
              return { Component: ReplenishmentSourcePage };
            },
          },
          {
            path: "distribute",
            handle: { breadcrumb: "Distribute items" },
            async lazy() {
              const { ReplenishmentDistributePage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/replenishment/replenishment-distribute-page"
              );
              return { Component: ReplenishmentDistributePage };
            },
          },
          {
            path: "putback",
            handle: { breadcrumb: "Put back items" },
            async lazy() {
              const { ReplenishmentPutbackPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/replenishment/replenishment-putback-page"
              );
              return { Component: ReplenishmentPutbackPage };
            },
          },
        ],
      },
      {
        path: "sku-lookup",
        handle: { breadcrumb: "SKU lookup" },
        async lazy() {
          const { SkuLookupPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/sku-lookup/sku-lookup-page"
          );
          return { Component: SkuLookupPage };
        },
      },
      {
        path: "bin-assignment",
        handle: { breadcrumb: "Bin assignment" },
        children: [
          {
            index: true,
            async lazy() {
              const { BinAssignmentPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/bin-assignment/bin-assignment-page"
              );
              return { Component: BinAssignmentPage };
            },
          },
        ],
      },
      {
        path: "product-conversion",
        handle: { breadcrumb: "Product conversion" },
        async lazy() {
          const { ProductConversionLayout } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/product-conversion/product-conversion-layout"
          );
          return { Component: ProductConversionLayout };
        },
        children: [
          {
            index: true,
            async lazy() {
              const { ProductConversionPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/product-conversion/product-conversion-page"
              );
              return { Component: ProductConversionPage };
            },
          },
        ],
      },
      {
        path: "work-order-station",
        handle: { breadcrumb: "Work order station" },
        async lazy() {
          const { WorkOrderStationLayout } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/work-order-station/work-order-station-layout"
          );
          return { Component: WorkOrderStationLayout };
        },
        children: [
          {
            index: true,
            async lazy() {
              const { WorkOrderStationPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/work-order-station/work-order-station-page"
              );
              return { Component: WorkOrderStationPage };
            },
          },
          {
            path: "bulk-pick",
            handle: { breadcrumb: "Bulk pick" },
            async lazy() {
              const { BulkPickPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/work-order-station/bulk-pick-page"
              );
              return { Component: BulkPickPage };
            },
          },
          {
            path: "batch/:batchId",
            handle: { breadcrumb: "Batch" },
            async lazy() {
              const { BatchDetailPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/work-order-station/batch-detail-page"
              );
              return { Component: BatchDetailPage };
            },
          },
          {
            path: "bulk-place",
            handle: { breadcrumb: "Bulk place" },
            async lazy() {
              const { BulkPlacePage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/work-order-station/bulk-place-page"
              );
              return { Component: BulkPlacePage };
            },
          },
        ],
      },
      {
        path: "print-label",
        handle: {
          breadcrumb: "Print label",
          permission: [
            ReturnsAndClaimsPermissions.VIEW_RETURN,
            ImsPermissions.ACCESS_INVENTORY,
          ],
        },
        children: [
          {
            async lazy() {
              const { ReturnsProcessingProviders } = await import(
                "@redotech/redo-merchant-returns-processing-app/returns-processing-providers"
              );
              return { Component: ReturnsProcessingProviders };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { PrintLabelPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/print-label-page"
                  );
                  return { Component: PrintLabelPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "consolidation",
        handle: { breadcrumb: "Consolidation" },
        async lazy() {
          const { ConsolidationPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/consolidation/consolidation-page"
          );
          return { Component: ConsolidationPage };
        },
      },
      {
        path: "flush",
        handle: {
          breadcrumb: "Flush",
          permission: [ImsPermissions.ACCESS_TRANSFERS],
        },
        children: [
          {
            index: true,
            async lazy() {
              const { FlushHomePage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/flush/flush-home-page"
              );
              return { Component: FlushHomePage };
            },
          },
          {
            path: "start",
            handle: { breadcrumb: "Start flush" },
            async lazy() {
              const { FlushStartPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/flush/flush-start-page"
              );
              return { Component: FlushStartPage };
            },
          },
          {
            path: ":flushSessionId",
            handle: { breadcrumb: "Scan items" },
            async lazy() {
              const { FlushScanPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/flush/flush-scan-page"
              );
              return { Component: FlushScanPage };
            },
          },
        ],
      },
      {
        path: "processing",
        handle: {
          breadcrumb: "Returns processing",
          permission: [
            ReturnsAndClaimsPermissions.VIEW_RETURN,
            ReturnsAndClaimsPermissions.RECEIVE_RETURN,
            ImsPermissions.ACCESS_INVENTORY,
          ],
        },
        children: [
          {
            async lazy() {
              const { ReturnsProcessingProviders } = await import(
                "@redotech/redo-merchant-returns-processing-app/returns-processing-providers"
              );
              return { Component: ReturnsProcessingProviders };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { StationProcessingEntryPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/station-processing-entry-page"
                  );
                  return { Component: StationProcessingEntryPage };
                },
              },
              {
                path: ":returnLabel",
                async lazy() {
                  const { ProcessingProviders } = await import(
                    "@redotech/redo-merchant-returns-processing-app/processing-providers"
                  );
                  return { Component: ProcessingProviders };
                },
                children: [
                  {
                    index: true,
                    handle: { breadcrumb: "Process return" },
                    async lazy() {
                      const { ReturnProcessingFlowPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/processing-page-flow"
                      );
                      return { Component: ReturnProcessingFlowPage };
                    },
                  },
                  {
                    path: "flow",
                    handle: { breadcrumb: "Return flow" },
                    async lazy() {
                      const { FlowStepPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/flow-step-page"
                      );
                      return { Component: FlowStepPage };
                    },
                  },
                  {
                    path: "flow/:sessionItemId",
                    handle: { breadcrumb: "Flow step" },
                    async lazy() {
                      const { FlowStepPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/flow-step-page"
                      );
                      return { Component: FlowStepPage };
                    },
                  },
                  {
                    path: "flow/license-plate/:sessionId",
                    handle: { breadcrumb: "License plate" },
                    async lazy() {
                      const { LpSessionPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/lp-session-page"
                      );
                      return { Component: LpSessionPage };
                    },
                  },
                  {
                    path: "flow/license-plate/:sessionId/:sessionItemId",
                    handle: { breadcrumb: "License plate" },
                    async lazy() {
                      const { LpSessionItemPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/lp-session-item-page"
                      );
                      return { Component: LpSessionItemPage };
                    },
                  },
                  {
                    path: "flow/sort/:sessionId",
                    handle: { breadcrumb: "Sort" },
                    async lazy() {
                      const { SortSessionPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/sort-session-page"
                      );
                      return { Component: SortSessionPage };
                    },
                  },
                  {
                    path: "flow/sort/:sessionId/:sessionItemId",
                    handle: { breadcrumb: "Sort" },
                    async lazy() {
                      const { SortSessionItemPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/sort-session-item-page"
                      );
                      return { Component: SortSessionItemPage };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "item-processing",
        handle: {
          breadcrumb: "Item processing",
          permission: [
            ReturnsAndClaimsPermissions.VIEW_RETURN,
            ReturnsAndClaimsPermissions.RECEIVE_RETURN,
            ImsPermissions.ACCESS_INVENTORY,
          ],
        },
        children: [
          {
            async lazy() {
              const { ReturnsProcessingProviders } = await import(
                "@redotech/redo-merchant-returns-processing-app/returns-processing-providers"
              );
              return { Component: ReturnsProcessingProviders };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { ItemProcessingLookupPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/item-processing-lookup-page"
                  );
                  return { Component: ItemProcessingLookupPage };
                },
              },
              {
                path: "generic-item",
                handle: { breadcrumb: "Generic item" },
                async lazy() {
                  const { ItemProcessingGenericItemPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/item-processing-generic-item-page"
                  );
                  return { Component: ItemProcessingGenericItemPage };
                },
              },
              {
                path: ":sessionId",
                async lazy() {
                  const { ItemProcessingProviders } = await import(
                    "@redotech/redo-merchant-returns-processing-app/item-processing-providers"
                  );
                  return { Component: ItemProcessingProviders };
                },
                children: [
                  {
                    index: true,
                    handle: { breadcrumb: "Process item" },
                    async lazy() {
                      const { ItemProcessingSessionPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/item-processing-session-page"
                      );
                      return { Component: ItemProcessingSessionPage };
                    },
                  },
                  {
                    path: "flow",
                    handle: { breadcrumb: "Item flow" },
                    async lazy() {
                      const { ItemProcessingFlowStepPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/item-processing-flow-step-page"
                      );
                      return { Component: ItemProcessingFlowStepPage };
                    },
                  },
                  {
                    path: "flow/license-plate",
                    handle: { breadcrumb: "License plate" },
                    async lazy() {
                      const { ItemProcessingLpPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/item-processing-lp-page"
                      );
                      return { Component: ItemProcessingLpPage };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "processing-flow",
        handle: {
          breadcrumb: "Returns processing flow",
          permission: [
            ReturnsAndClaimsPermissions.VIEW_RETURN,
            ReturnsAndClaimsPermissions.RECEIVE_RETURN,
            ImsPermissions.ACCESS_INVENTORY,
          ],
        },
        children: [
          {
            async lazy() {
              const { ReturnsProcessingProviders } = await import(
                "@redotech/redo-merchant-returns-processing-app/returns-processing-providers"
              );
              return { Component: ReturnsProcessingProviders };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { StationProcessingEntryPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/station-processing-entry-page"
                  );
                  return { Component: StationProcessingEntryPage };
                },
              },
              {
                path: ":returnLabel",
                async lazy() {
                  const { ProcessingProviders } = await import(
                    "@redotech/redo-merchant-returns-processing-app/processing-providers"
                  );
                  return { Component: ProcessingProviders };
                },
                children: [
                  {
                    index: true,
                    handle: { breadcrumb: "Process return" },
                    async lazy() {
                      const { ReturnProcessingFlowPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/processing-page-flow"
                      );
                      return { Component: ReturnProcessingFlowPage };
                    },
                  },
                  {
                    path: "flow",
                    handle: { breadcrumb: "Return flow" },
                    async lazy() {
                      const { FlowStepPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/flow-step-page"
                      );
                      return { Component: FlowStepPage };
                    },
                  },
                  {
                    path: "flow/:sessionItemId",
                    handle: { breadcrumb: "Flow step" },
                    async lazy() {
                      const { FlowStepPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/flow-step-page"
                      );
                      return { Component: FlowStepPage };
                    },
                  },
                  {
                    path: "flow/license-plate/:sessionId",
                    handle: { breadcrumb: "License plate" },
                    async lazy() {
                      const { LpSessionPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/lp-session-page"
                      );
                      return { Component: LpSessionPage };
                    },
                  },
                  {
                    path: "flow/license-plate/:sessionId/:sessionItemId",
                    handle: { breadcrumb: "License plate" },
                    async lazy() {
                      const { LpSessionItemPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/lp-session-item-page"
                      );
                      return { Component: LpSessionItemPage };
                    },
                  },
                  {
                    path: "flow/sort/:sessionId",
                    handle: { breadcrumb: "Sort" },
                    async lazy() {
                      const { SortSessionPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/sort-session-page"
                      );
                      return { Component: SortSessionPage };
                    },
                  },
                  {
                    path: "flow/sort/:sessionId/:sessionItemId",
                    handle: { breadcrumb: "Sort" },
                    async lazy() {
                      const { SortSessionItemPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/sort-session-item-page"
                      );
                      return { Component: SortSessionItemPage };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "sorting",
        handle: {
          breadcrumb: "Returns sorting",
          permission: [
            ReturnsAndClaimsPermissions.VIEW_RETURN,
            ReturnsAndClaimsPermissions.RECEIVE_RETURN,
            ImsPermissions.ACCESS_INVENTORY,
          ],
        },
        children: [
          {
            async lazy() {
              const { ReturnsProcessingProviders } = await import(
                "@redotech/redo-merchant-returns-processing-app/returns-processing-providers"
              );
              return { Component: ReturnsProcessingProviders };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { ReturnSortingPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/sorting-page"
                  );
                  return { Component: ReturnSortingPage };
                },
              },
              {
                path: "quarantine",
                handle: { breadcrumb: "Quarantine item" },
                async lazy() {
                  const { QuarantinePage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/quarantine-page"
                  );
                  return { Component: QuarantinePage };
                },
              },
              {
                path: ":returnId",
                handle: { breadcrumb: "Sort return" },
                async lazy() {
                  const { SortingDetailPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/sorting-detail-page"
                  );
                  return { Component: SortingDetailPage };
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
